import {
  GridFilterModel,
  GridSortModel,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  AdvancedSearchQuery,
  SearchRule,
  FieldConfig,
  FieldOperator,
} from '../components/TableSearch/types';
import { Column } from '../components/LgmDataTable';

/**
 * Maps DataGrid filter operators to our FieldOperator type
 * Only maps operators that are actually supported by our backend (see api/sql/advancedSearchBuilder.js)
 * Supported: contains, equals, notEquals, greaterThan, lessThan, isBefore, isAfter, isBetween
 */
function mapGridOperatorToFilterOperator(gridOp: string): FieldOperator {
  const mapping: Record<string, FieldOperator> = {
    // Direct mappings to backend operators (used by our custom filter operators)
    contains: 'contains',
    equals: 'equals',
    notEquals: 'notEquals',
    greaterThan: 'greaterThan',
    lessThan: 'lessThan',
    isBefore: 'isBefore',
    isAfter: 'isAfter',
    isBetween: 'isBetween',
  };

  const result = mapping[gridOp];

  if (!result) {
    console.warn(
      `Unsupported DataGrid operator: "${gridOp}". Only these operators are supported:`,
      Object.keys(mapping).join(', ')
    );
    // Default to 'contains' for backwards compatibility
    return 'contains';
  }

  return result;
}

/**
 * Converts DataGrid's GridFilterModel to our AdvancedSearchQuery format
 * This allows DataGrid column filters to work with our existing server-side API
 */
export function gridFilterToAdvancedQuery(
  filterModel: GridFilterModel,
  _: FieldConfig[]
): AdvancedSearchQuery {
  if (!filterModel.items || filterModel.items.length === 0) {
    return {
      filterGroups: [],
      groupOperator: 'AND',
    };
  }

  const rules: SearchRule[] = filterModel.items
    .filter(item => {
      // Filter out items without value or with empty string value
      if (item.value === undefined || item.value === null) return false;
      if (typeof item.value === 'string' && item.value.trim() === '')
        return false;

      // Special validation for isBetween operator
      // Must have both start and end values to be valid
      if (item.operator === 'isBetween') {
        if (!Array.isArray(item.value)) return false;
        if (item.value.length !== 2) return false;
        // Check that both values are present and non-empty
        const [start, end] = item.value;
        if (!start || !end) return false;
        if (typeof start === 'string' && start.trim() === '') return false;
        if (typeof end === 'string' && end.trim() === '') return false;
      }

      return true;
    })
    .map((item, index) => {
      // Debug logging to see what MUI DataGrid is sending
      return {
        id: `grid_filter_${index}`,
        field: item.field,
        operator: mapGridOperatorToFilterOperator(item.operator || 'contains'),
        value: item.value,
      };
    });

  // If no valid rules after filtering, return empty query
  if (rules.length === 0) {
    return {
      filterGroups: [],
      groupOperator: 'AND',
    };
  }

  return {
    filterGroups: [
      {
        id: 'grid_filters',
        logicalOperator: filterModel.logicOperator === 'or' ? 'OR' : 'AND',
        rules: rules,
      },
    ],
    groupOperator: 'AND',
  };
}

/**
 * Merges two AdvancedSearchQuery objects with AND logic
 * Used when combining TableSearch advanced filters with DataGrid column filters
 * Filters out empty groups (groups with no rules) during merge
 */
export function mergeAdvancedQueries(
  query1: AdvancedSearchQuery,
  query2: AdvancedSearchQuery
): AdvancedSearchQuery {
  // Filter out empty groups from both queries
  const nonEmptyGroups1 =
    query1?.filterGroups?.filter(
      group => group.rules && group.rules.length > 0
    ) || [];
  const nonEmptyGroups2 =
    query2?.filterGroups?.filter(
      group => group.rules && group.rules.length > 0
    ) || [];

  // If one is empty, return the other
  if (nonEmptyGroups1.length === 0 && nonEmptyGroups2.length === 0) {
    return { filterGroups: [], groupOperator: 'AND' };
  }
  if (nonEmptyGroups1.length === 0) {
    return { filterGroups: nonEmptyGroups2, groupOperator: 'AND' };
  }
  if (nonEmptyGroups2.length === 0) {
    return { filterGroups: nonEmptyGroups1, groupOperator: 'AND' };
  }

  // Merge non-empty groups with AND combinator
  return {
    filterGroups: [...nonEmptyGroups1, ...nonEmptyGroups2],
    groupOperator: 'AND',
  };
}

/**
 * Converts our sort config to DataGrid's GridSortModel
 */
export function sortConfigToGridSortModel<T>(
  sortConfig: {
    key: keyof T;
    direction: 'ASC' | 'DESC';
  } | null
): GridSortModel {
  if (!sortConfig) return [];
  return [
    {
      field: String(sortConfig.key),
      sort: sortConfig.direction.toLowerCase() as 'asc' | 'desc',
    },
  ];
}

/**
 * Converts DataGrid's GridSortModel to our sort config
 */
export function gridSortModelToSortConfig<T>(
  sortModel: GridSortModel
): { key: keyof T; direction: 'ASC' | 'DESC' } | null {
  if (!sortModel || sortModel.length === 0) return null;
  const firstSort = sortModel[0];
  return {
    key: firstSort.field as keyof T,
    direction: firstSort.sort === 'desc' ? 'DESC' : 'ASC',
  };
}

/**
 * Transforms our Column type to DataGrid's GridColDef
 * Preserves custom rendering and tooltip functionality
 */
export function transformToGridColDef<T>(
  column: Column<T>,
  renderCell?: (params: GridRenderCellParams) => React.ReactNode
): GridColDef {
  // Parse width - convert percentage to pixels estimate (assumes 1400px container)
  let widthValue: number | undefined;
  if (typeof column.width === 'string' && column.width.endsWith('%')) {
    const percentage = parseFloat(column.width);
    widthValue = Math.round((percentage / 100) * 1400); // Rough estimate
  } else if (typeof column.width === 'number') {
    widthValue = column.width;
  }

  return {
    field: String(column.key),
    headerName: column.label,
    width: widthValue || 150,
    minWidth: 100,
    flex: widthValue ? undefined : 1, // Use flex if no explicit width
    sortable: true,
    resizable: true,
    filterable: true,
    hideable: true,
    description: column.tooltip,
    renderCell: renderCell,
  };
}

/**
 * Creates default column visibility model
 * All columns visible by default
 */
export function createDefaultColumnVisibility<T>(
  columns: Column<T>[]
): Record<string, boolean> {
  return columns.reduce(
    (acc, col) => {
      acc[String(col.key)] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
}
